import { useState } from 'react';
import { useOrders } from '../../useOrders';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { OrderImagesCarousel } from '../../OrderImagesCarousel';
import { CustomButton } from '../../../../../../../utils/Button';
import {
  createPaystackMandate,
  getAllLinkedAccounts,
} from '../../../../../../../api/orderAPI';
import { formatCurrency } from '../../../../../../../utils/FormatCurrency';
import { LinkedAccounts } from '../../../../../../checkoutItems/directDebitSetup/directDebitBankSetUp/directDebitSetupForm/makeDirectDebit/LinkedAccounts';

export const PendingOrdersCard = ({ order }) => {
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedBankAuthCode, setSelectedBankAuthCode] = useState(null);
  const navigate = useNavigate();

  const paystackOrderReference = order?.payments[0]?.reference;
  const { data: linkedAccounts, isFetching } = useQuery({
    queryKey: ['linkedAccounts'],
    queryFn: () => getAllLinkedAccounts(),
  });

  const downPaymentSuccess = order?.mandateStatus === 'pending';

  const shouldIncludeAuthCode =
    linkedAccounts?.has_existing_mandates && selectedAccountId;

  const { mutate: createMandate, isPending: isValidating } = useMutation({
    mutationFn: () => {
      let mandateParams = { reference: paystackOrderReference };

      if (shouldIncludeAuthCode) {
        mandateParams = {
          reference: paystackOrderReference,
          authorization_code: selectedBankAuthCode,
        };
      } else if (!shouldIncludeAuthCode) {
        mandateParams = {
          reference: paystackOrderReference,
        };
      }

      return createPaystackMandate(mandateParams);
    },

    onSuccess: (res) => {
      const redirectUrl = res.data?.redirect_url;

      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      if (res.success === true) {
        navigate(
          `/cart-items/checkout/direct-debit/success?orderid=${order?.orderNumber}`
        );
      }
    },
    onError: (error) => {
      toast.error(
        error?.data?.message || 'Failed to create mandate. Please try again.'
      );
    },
  });

  const handleCreateMandate = () => {
    if (!downPaymentSuccess) return;
    createMandate();
  };

  return (
    <div className=" mb-4 grid md:flex md:justify-between gap-4 border-b pb-4">
      <div className="bg-[#F6F6F6] rounded-md p-2 lg:w-2/3">
        <div className="flex justify-between">
          <div
            key={order?.orderNumber || index}
            className="flex items-center py-3 px-2 justify-between text-balance w-full"
          >
            <div className="flex items-ceter justify-start lg:justify-between space-x-3">
              <div className="hidden lg:block">
                <OrderImagesCarousel orders={order} />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <p className="text-[11px] flex flex-col font-normal ">
                  Order number
                  <span className="font-semibold text-base">
                    {order?.orderNumber}
                  </span>
                </p>
                <p className="text-[11px] flex flex-col">
                  Order amount
                  <span className="font-semibold text-base">
                    {formatCurrency(order?.totalAmount)}
                  </span>
                </p>
                <p className="text-[11px] flex flex-col">
                  Direct debit status
                  <span className="font-semibold text-base">
                    Not yet initiated
                  </span>
                </p>

                {/* Second line */}
                <p className="font-medium text-[11px] flex flex-col">
                  Number of installments
                  <span className="font-semibold text-base">
                    {order?.installmentCount}
                  </span>
                </p>
                <p className="font-medium text-[11px] flex flex-col">
                  Downpayment
                  <span className="font-semibold text-base">
                    {formatCurrency(order?.paymentSummary?.total_amount_paid)}
                  </span>
                </p>

                <ul className="font-semibold text-[11px]">
                  Item(s)
                  {order?.items?.map((item, idx) => (
                    <li
                      key={idx}
                      className="text-[14px] font-semibold font-duplet list-none"
                    >
                      {item?.productName}
                    </li>
                  ))}
                </ul>

                <p className="text-sm  flex flex-col text-[11px]">
                  Order Date
                  <span className="font-semibold text-base">
                    {new Date(order?.createdAt).toLocaleDateString()}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/3 flex flex-col justify-center items-center">
        {linkedAccounts?.has_existing_mandates && (
          <article className="justify-start items-start flex flex-col ">
            <p className="text-xs mb-2 text-center  w-full">
              Select your linked bank account to continue
            </p>
            {isFetching && (
              <p className="skeleton w-[265px] md:w-[300px] h-14"></p>
            )}
            <LinkedAccounts
              linkedAccounts={linkedAccounts}
              downPaymentSuccess={true}
              selectedAccountId={selectedAccountId}
              setSelectedAccountId={setSelectedAccountId}
              setSelectedBankAuthCode={setSelectedBankAuthCode}
            />
          </article>
        )}
        {linkedAccounts?.has_existing_mandates && (
          <div className="w-2/3 lg:w-full ">
            <CustomButton
              width="100%"
              text={isValidating ? 'Creating...' : 'Continue'}
              onClick={handleCreateMandate}
              disabled={isValidating || !selectedAccountId}
              bgColor="var(--yellow-primary)"
              className="text-[#E8EBEA] "
            />
          </div>
        )}

        {!linkedAccounts?.has_existing_mandates && (
          <div className="w-full">
            <CustomButton
              width="100%"
              text={isValidating ? 'Setting up...' : 'Set up Direct Debit'}
              onClick={handleCreateMandate}
              disabled={isValidating || !downPaymentSuccess}
              bgColor={downPaymentSuccess ? 'var(--yellow-primary)' : '#F6F6F6'}
              className={!downPaymentSuccess && 'text-[#E8EBEA] '}
            />
          </div>
        )}
      </div>
    </div>
  );
};
